/**
 * Room API module for communicating with the backend
 */

import type { GuestDays, RoomMemberSummary, RoomRole } from 'minka-shared';
import type { CreateRoomData, RoomResponse, RoomInfo } from '@/types/room.types';
import config from '@/env-config';
import { formatApiErrorMessage } from '@/utils/api-error';

/**
 * An API refusal the UI has to tell apart from any other failure — the last
 * admin, a taken organization name, a lapsed guest. The message is already
 * user-facing; `code` is what the caller branches on.
 */
export class RoomApiError extends Error {
    constructor(
        message: string,
        public readonly status: number,
        public readonly code?: string
    ) {
        super(message);
        this.name = 'RoomApiError';
    }
}

/** Read one API response, raising a `RoomApiError` that carries the error code. */
async function readResponse<T>(response: Response, fallback: string): Promise<T> {
    const body = await response.json().catch(() => ({}));

    if (!response.ok) {
        throw new RoomApiError(
            formatApiErrorMessage(body, fallback),
            response.status,
            typeof body?.code === 'string' ? body.code : undefined
        );
    }

    return body as T;
}

class RoomAPI {
    /**
     * Get authentication headers with JWT token
     */
    private getAuthHeaders(): HeadersInit {
        const token = localStorage.getItem('jwt_token');
        return {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        };
    }

    /**
     * Get all rooms for the current user
     */
    async getUserRooms(): Promise<{ success: boolean; data: { rooms: RoomInfo[] }; error?: string }> {
        try {
            const response = await fetch(`${config.api.baseUrl}/rooms`, {
                method: 'GET',
                headers: this.getAuthHeaders()
            });

            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.error || `Failed to fetch rooms: ${response.status}`);
            }

            return data;
        } catch (error) {
            console.error('Error fetching user rooms:', error);
            throw error;
        }
    }

    /**
     * Create a new room
     */
    async createRoom(roomData: CreateRoomData): Promise<RoomResponse> {
        try {
            const response = await fetch(`${config.api.baseUrl}/rooms`, {
                method: 'POST',
                headers: this.getAuthHeaders(),
                body: JSON.stringify(roomData)
            });

            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.error || `Failed to create room: ${response.status}`);
            }

            return data;
        } catch (error) {
            console.error('Error creating room:', error);
            throw error;
        }
    }

    /**
     * Delete a room
     */
    async deleteRoom(roomId: string): Promise<RoomResponse> {
        try {
            const response = await fetch(`${config.api.baseUrl}/rooms/${roomId}`, {
                method: 'DELETE',
                headers: this.getAuthHeaders()
            });

            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.error || `Failed to delete room: ${response.status}`);
            }

            return data;
        } catch (error) {
            console.error('Error deleting room:', error);
            throw error;
        }
    }

    /**
     * Get room details including player information
     */
    async getRoomDetails(roomId: string): Promise<{ 
        success: boolean; 
        data?: { 
            room: any; 
            players: any[] 
        }; 
        error?: string 
    }> {
        try {
            const response = await fetch(`${config.api.baseUrl}/rooms/${roomId}`, {
                method: 'GET',
                headers: this.getAuthHeaders()
            });

            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.error || `Failed to get room details: ${response.status}`);
            }

            return data;
        } catch (error) {
            console.error('Error getting room details:', error);
            throw error;
        }
    }

    /**
     * Update a room's name and/or description (admin only)
     */
    async updateRoom(roomId: string, updateData: { name?: string; description?: string; maxPlayers?: number }): Promise<RoomResponse> {
        try {
            const response = await fetch(`${config.api.baseUrl}/rooms/${roomId}`, {
                method: 'PUT',
                headers: this.getAuthHeaders(),
                body: JSON.stringify(updateData)
            });

            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.error || `Failed to update room: ${response.status}`);
            }

            return data;
        } catch (error) {
            console.error('Error updating room:', error);
            throw error;
        }
    }

    /**
     * Claim a zone for the current user
     */
    async claimZone(roomId: string, zoneId: string): Promise<{
        success: boolean;
        message?: string;
        error?: string;
    }> {
        try {
            const response = await fetch(`${config.api.baseUrl}/rooms/${roomId}/zones/${zoneId}/claim`, {
                method: 'POST',
                headers: this.getAuthHeaders()
            });

            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.error || `Failed to claim zone: ${response.status}`);
            }

            return data;
        } catch (error) {
            console.error('Error claiming zone:', error);
            throw error;
        }
    }

    /**
     * Unclaim a zone for the current user
     */
    async unclaimZone(roomId: string, zoneId: string): Promise<{
        success: boolean;
        message?: string;
        error?: string;
    }> {
        try {
            const response = await fetch(`${config.api.baseUrl}/rooms/${roomId}/zones/${zoneId}/claim`, {
                method: 'DELETE',
                headers: this.getAuthHeaders()
            });

            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.error || `Failed to unclaim zone: ${response.status}`);
            }

            return data;
        } catch (error) {
            console.error('Error unclaiming zone:', error);
            throw error;
        }
    }

    /**
     * Admin unclaim a zone for any user (admin only)
     */
    async adminUnclaimZone(roomId: string, zoneId: string, userId: string): Promise<{
        success: boolean;
        message?: string;
        error?: string;
    }> {
        try {
            const response = await fetch(`${config.api.baseUrl}/rooms/${roomId}/zones/${zoneId}/claim/${userId}`, {
                method: 'DELETE',
                headers: this.getAuthHeaders()
            });

            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.error || `Failed to unclaim zone: ${response.status}`);
            }

            return data;
        } catch (error) {
            console.error('Error admin unclaiming zone:', error);
            throw error;
        }
    }

    /**
     * Everyone holding a permission on an island, connected or not — what the
     * member list manages, as opposed to the live session view in `players`.
     */
    async getRoomMembers(roomId: string): Promise<RoomMemberSummary[]> {
        const response = await fetch(`${config.api.baseUrl}/rooms/${roomId}`, {
            method: 'GET',
            headers: this.getAuthHeaders()
        });

        const body = await readResponse<{ data?: { members?: RoomMemberSummary[] } }>(
            response,
            'Failed to load island members'
        );

        return body.data?.members ?? [];
    }

    /**
     * Promote or demote a member. Guests become residents or admins (which
     * clears their expiry); nothing is ever demoted *to* guest, since guest
     * access arrives with an invitation.
     */
    async updateMemberRole(roomId: string, userId: string, role: RoomRole): Promise<void> {
        const response = await fetch(
            `${config.api.baseUrl}/rooms/${roomId}/permissions/${userId}`,
            {
                method: 'PUT',
                headers: this.getAuthHeaders(),
                body: JSON.stringify({ role })
            }
        );

        await readResponse(response, 'Failed to change the member\'s role');
    }

    /** Push a guest's expiry out to `days` from now (not from the old expiry). */
    async extendGuest(roomId: string, userId: string, days: GuestDays): Promise<void> {
        const response = await fetch(
            `${config.api.baseUrl}/rooms/${roomId}/permissions/${userId}`,
            {
                method: 'PUT',
                headers: this.getAuthHeaders(),
                body: JSON.stringify({ extendGuestDays: days })
            }
        );

        await readResponse(response, 'Failed to extend guest access');
    }

    /** Revoke somebody else's permission on an island (admins only). */
    async removeMember(roomId: string, userId: string): Promise<void> {
        const response = await fetch(
            `${config.api.baseUrl}/rooms/${roomId}/permissions/${userId}`,
            {
                method: 'DELETE',
                headers: this.getAuthHeaders()
            }
        );

        await readResponse(response, 'Failed to remove the member');
    }

    /** Give up your own permission on an island. 409 `last_admin` if you are it. */
    async leaveIsland(roomId: string): Promise<void> {
        const response = await fetch(`${config.api.baseUrl}/rooms/${roomId}/leave`, {
            method: 'POST',
            headers: this.getAuthHeaders()
        });

        await readResponse(response, 'Failed to leave the island');
    }
}

// Create and export a singleton instance
const roomAPI = new RoomAPI();
export default roomAPI;